import React from 'react';

interface TrophyBadgeProps {
  rank: number;
  size?: 'small' | 'large';
  title?: string;
}

export const TrophyBadge: React.FC<TrophyBadgeProps> = ({ rank, size = 'small', title }) => {
  // 크기 설정
  const dimensions = size === 'large' ? { width: 28, height: 28, fontSize: 12 } : { width: 22, height: 22, fontSize: 10 };
  
  // 등급별 스타일 설정
  const getStyle = (rank: number) => {
    const styles = {
      1: { 
        bg: 'linear-gradient(135deg, #FFD700, #FFA500, #FFFF99, #FFD700)', 
        shadow: '0 0 20px #FFD700, 0 0 40px #FFA500',
        border: '3px solid #FFFF99',
        ribbonColor: 'linear-gradient(135deg, #8B0000, #DC143C, #FF1493)',
      },
      2: { 
        bg: 'linear-gradient(135deg, #E6E6FA, #C0C0C0, #F8F8FF, #C0C0C0)', 
        shadow: '0 0 15px #C0C0C0, 0 0 30px #E6E6FA',
        border: '3px solid #F8F8FF',
        ribbonColor: 'linear-gradient(135deg, #000080, #4169E1, #87CEEB)',
      },
      3: { 
        bg: 'linear-gradient(135deg, #DEB887, #CD7F32, #F4A460, #CD7F32)', 
        shadow: '0 0 12px #CD7F32, 0 0 25px #DEB887',
        border: '3px solid #F4A460',
        ribbonColor: 'linear-gradient(135deg, #8B4513, #A0522D, #D2691E)',
      },
      4: { bg: 'linear-gradient(135deg, #87CEEB, #4682B4)', shadow: '0 0 8px #87CEEB', border: '2px solid rgba(255, 255, 255, 0.3)', ribbonColor: 'linear-gradient(135deg, #DC143C, #B22222)' },
      5: { bg: 'linear-gradient(135deg, #DDA0DD, #BA55D3)', shadow: '0 0 8px #DDA0DD', border: '2px solid rgba(255, 255, 255, 0.3)', ribbonColor: 'linear-gradient(135deg, #DC143C, #B22222)' },
      6: { bg: 'linear-gradient(135deg, #98FB98, #32CD32)', shadow: '0 0 8px #98FB98', border: '2px solid rgba(255, 255, 255, 0.3)', ribbonColor: 'linear-gradient(135deg, #DC143C, #B22222)' },
      7: { bg: 'linear-gradient(135deg, #F0E68C, #DAA520)', shadow: '0 0 6px #F0E68C', border: '2px solid rgba(255, 255, 255, 0.3)', ribbonColor: 'linear-gradient(135deg, #DC143C, #B22222)' },
      8: { bg: 'linear-gradient(135deg, #FFA07A, #FF6347)', shadow: '0 0 6px #FFA07A', border: '2px solid rgba(255, 255, 255, 0.3)', ribbonColor: 'linear-gradient(135deg, #DC143C, #B22222)' },
      9: { bg: 'linear-gradient(135deg, #20B2AA, #008B8B)', shadow: '0 0 6px #20B2AA', border: '2px solid rgba(255, 255, 255, 0.3)', ribbonColor: 'linear-gradient(135deg, #DC143C, #B22222)' },
      10: { bg: 'linear-gradient(135deg, #778899, #696969)', shadow: '0 0 6px #778899', border: '2px solid rgba(255, 255, 255, 0.3)', ribbonColor: 'linear-gradient(135deg, #DC143C, #B22222)' },
    };
    return styles[rank as keyof typeof styles] || styles[10];
  };

  const style = getStyle(rank);

  return (
    <div 
      className={`medal-container rank-${rank}`}
      title={title}
      style={{
        position: 'relative',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginLeft: size === 'small' ? '6px' : '8px',
      }}
    >
      {/* 메달 리본 (V자 모양) */}
      <div 
        style={{
          position: 'relative',
          width: `${Math.round(dimensions.width * (rank === 1 ? 1.0 : 0.8))}px`,
          height: `${Math.round(dimensions.height * (rank === 1 ? 0.8 : 0.6))}px`,
          marginBottom: `-${Math.round(dimensions.height * 0.2)}px`,
          zIndex: 1,
        }}
      >
        {rank <= 3 && (
          <>
            {/* 1-3등 전용 - 추가 장식 리본 (뒤쪽) */}
            <div 
              style={{
                position: 'absolute',
                left: rank === 1 ? '10%' : '12%',
                top: rank === 1 ? '-2px' : '-1px',
                width: `${Math.round(dimensions.width * (rank === 1 ? 0.35 : 0.32))}px`,
                height: `${Math.round(dimensions.height * (rank === 1 ? 0.7 : 0.65))}px`,
                background: rank === 1 
                  ? 'linear-gradient(135deg, #FFD700, #FFA500, #FFFF99)'
                  : rank === 2 
                    ? 'linear-gradient(135deg, #E6E6FA, #C0C0C0, #F8F8FF)'
                    : 'linear-gradient(135deg, #DEB887, #CD7F32, #F4A460)',
                clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0 85%)',
                border: rank === 1 
                  ? '2px solid #FFFF99' 
                  : rank === 2 
                    ? '2px solid #F8F8FF'
                    : '2px solid #F4A460',
                boxShadow: rank === 1 
                  ? 'inset 0 2px 4px rgba(255, 255, 255, 0.6), 0 3px 6px rgba(255, 215, 0, 0.4)'
                  : rank === 2 
                    ? 'inset 0 2px 4px rgba(255, 255, 255, 0.5), 0 3px 6px rgba(192, 192, 192, 0.3)'
                    : 'inset 0 2px 4px rgba(255, 255, 255, 0.4), 0 3px 6px rgba(205, 127, 50, 0.3)',
                zIndex: -1,
              }}
            />
            <div 
              style={{
                position: 'absolute',
                right: rank === 1 ? '10%' : '12%',
                top: rank === 1 ? '-2px' : '-1px',
                width: `${Math.round(dimensions.width * (rank === 1 ? 0.35 : 0.32))}px`,
                height: `${Math.round(dimensions.height * (rank === 1 ? 0.7 : 0.65))}px`,
                background: rank === 1 
                  ? 'linear-gradient(135deg, #FFD700, #FFA500, #FFFF99)'
                  : rank === 2 
                    ? 'linear-gradient(135deg, #E6E6FA, #C0C0C0, #F8F8FF)'
                    : 'linear-gradient(135deg, #DEB887, #CD7F32, #F4A460)',
                clipPath: 'polygon(0 0, 100% 0, 100% 85%, 15% 100%)',
                border: rank === 1 
                  ? '2px solid #FFFF99' 
                  : rank === 2 
                    ? '2px solid #F8F8FF'
                    : '2px solid #F4A460',
                boxShadow: rank === 1 
                  ? 'inset 0 2px 4px rgba(255, 255, 255, 0.6), 0 3px 6px rgba(255, 215, 0, 0.4)'
                  : rank === 2 
                    ? 'inset 0 2px 4px rgba(255, 255, 255, 0.5), 0 3px 6px rgba(192, 192, 192, 0.3)'
                    : 'inset 0 2px 4px rgba(255, 255, 255, 0.4), 0 3px 6px rgba(205, 127, 50, 0.3)',
                zIndex: -1,
              }}
            />
          </>
        )}
        
        {/* 왼쪽 리본 */}
        <div 
          style={{
            position: 'absolute',
            left: rank <= 3 ? '15%' : '20%',
            top: 0,
            width: `${Math.round(dimensions.width * (rank <= 3 ? 0.3 : 0.25))}px`,
            height: `${Math.round(dimensions.height * 0.6)}px`,
            background: style.ribbonColor,
            clipPath: 'polygon(0 0, 100% 0, 80% 100%, 0 80%)',
            border: rank <= 3 ? '2px solid rgba(255, 255, 255, 0.6)' : '1px solid rgba(255, 255, 255, 0.4)',
            boxShadow: rank === 1 
              ? 'inset 0 2px 4px rgba(255, 255, 255, 0.6), 0 3px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(255, 20, 147, 0.3)'
              : rank === 2
                ? 'inset 0 2px 4px rgba(255, 255, 255, 0.5), 0 3px 6px rgba(0, 0, 0, 0.4), 0 0 8px rgba(65, 105, 225, 0.3)'
                : rank === 3
                  ? 'inset 0 2px 4px rgba(255, 255, 255, 0.4), 0 3px 6px rgba(0, 0, 0, 0.4), 0 0 6px rgba(210, 105, 30, 0.3)'
                  : 'inset 0 1px 2px rgba(255, 255, 255, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3)',
          }}
        />
        
        {/* 오른쪽 리본 */}
        <div 
          style={{
            position: 'absolute',
            right: rank <= 3 ? '15%' : '20%',
            top: 0,
            width: `${Math.round(dimensions.width * (rank <= 3 ? 0.3 : 0.25))}px`,
            height: `${Math.round(dimensions.height * 0.6)}px`,
            background: style.ribbonColor,
            clipPath: 'polygon(0 0, 100% 0, 100% 80%, 20% 100%)',
            border: rank <= 3 ? '2px solid rgba(255, 255, 255, 0.6)' : '1px solid rgba(255, 255, 255, 0.4)',
            boxShadow: rank === 1 
              ? 'inset 0 2px 4px rgba(255, 255, 255, 0.6), 0 3px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(255, 20, 147, 0.3)'
              : rank === 2
                ? 'inset 0 2px 4px rgba(255, 255, 255, 0.5), 0 3px 6px rgba(0, 0, 0, 0.4), 0 0 8px rgba(65, 105, 225, 0.3)'
                : rank === 3
                  ? 'inset 0 2px 4px rgba(255, 255, 255, 0.4), 0 3px 6px rgba(0, 0, 0, 0.4), 0 0 6px rgba(210, 105, 30, 0.3)'
                  : 'inset 0 1px 2px rgba(255, 255, 255, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3)',
          }}
        />
        
        {rank <= 3 && (
          <>
            {/* 1-3등 전용 - 중앙 장식 */}
            <div 
              style={{
                position: 'absolute',
                left: '50%',
                top: rank === 1 ? '10%' : '15%',
                transform: 'translateX(-50%)',
                width: `${Math.round(dimensions.width * (rank === 1 ? 0.15 : 0.12))}px`,
                height: `${Math.round(dimensions.height * (rank === 1 ? 0.15 : 0.12))}px`,
                background: rank === 1 
                  ? 'radial-gradient(circle, #FFFF99, #FFD700)'
                  : rank === 2 
                    ? 'radial-gradient(circle, #F8F8FF, #C0C0C0)'
                    : 'radial-gradient(circle, #F4A460, #CD7F32)',
                borderRadius: '50%',
                border: rank === 1 
                  ? '1px solid #FFA500' 
                  : rank === 2 
                    ? '1px solid #A8A8A8'
                    : '1px solid #B8860B',
                boxShadow: rank === 1 
                  ? '0 0 6px rgba(255, 215, 0, 0.6), inset 0 1px 2px rgba(255, 255, 255, 0.8)'
                  : rank === 2 
                    ? '0 0 4px rgba(192, 192, 192, 0.5), inset 0 1px 2px rgba(255, 255, 255, 0.7)'
                    : '0 0 3px rgba(205, 127, 50, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.6)',
              }}
            />
          </>
        )}
      </div>
      
      {/* 메달 본체 */}
      <div 
        className={`medal-badge rank-${rank}`}
        style={{
          position: 'relative',
          width: `${dimensions.width}px`,
          height: `${dimensions.height}px`,
          background: style.bg,
          borderRadius: '50%',
          boxShadow: `${style.shadow}, inset 0 2px 4px rgba(255, 255, 255, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.3)`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: style.border,
          fontSize: `${dimensions.fontSize}px`,
          fontWeight: 'bold',
          color: 'white',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
          zIndex: 2,
          textAlign: 'center',
          lineHeight: 1,
          paddingRight: rank === 1 ? '2px' : rank <= 3 ? '1px' : '0px', // 1등 특별 조정
        }}
      >
        {rank}
      </div>
    </div>
  );
};
